import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchSiteContentSuccess } from '../redux/slices/siteContentSlice';
import { updateWidgetsWithMenuData, updateWidgetsWithComboData } from '../utils/widgetDataUpdater';
import type { MenuItem } from '../redux/slices/menuSlice';

interface UseWidgetDataUpdaterProps {
  siteContent: any;
  menuData: MenuItem[] | null;
  comboData: any[] | null;
  menuLoading: boolean;
  comboLoading: boolean;
}

export const useWidgetDataUpdater = ({
  siteContent,
  menuData,
  comboData,
  menuLoading,
  comboLoading,
}: UseWidgetDataUpdaterProps) => {
  const dispatch = useAppDispatch();
  
  // Restaurant selector
  const selectedRestaurant = useAppSelector((state) => state.restaurant.selectedRestaurant);
  
  const hasUpdatedWithData = useRef(false);

  useEffect(() => {
    // Early return if no selectedRestaurant
    if (!selectedRestaurant) return;
    
    // Only update if we have site content and at least one type of data
    if (!siteContent || hasUpdatedWithData.current) return;
    
    // Check if we have any data to update with
    const hasMenuData = menuData && !menuLoading;
    const hasComboData = comboData && !comboLoading;
    
    if (!hasMenuData && !hasComboData) return;

    // Check if we have widgets that need updating
    const hasPopularWidgets = siteContent.homeWidgets?.some(
      (widget: any) =>
        widget.type.toLowerCase().includes('popular') ||
        widget.type.toLowerCase().includes('popularitems')
    );

    const hasComboWidgets = siteContent.homeWidgets?.some(
      (widget: any) => widget.type.toLowerCase().includes('combo')
    );

    // If we don't have any widgets that need updating, mark as updated and return
    if (!hasPopularWidgets && !hasComboWidgets) {
      hasUpdatedWithData.current = true;
      return;
    }

    // Only proceed if we have the data for the widgets we need to update
    if ((hasPopularWidgets && !hasMenuData) || (hasComboWidgets && !hasComboData)) {
      return; // Wait for all required data
    }

    
    hasUpdatedWithData.current = true;

    let updatedWidgets = siteContent.widgets;
    let updatedHomeWidgets = siteContent.homeWidgets;

    // Apply menu data updates if available
    if (hasMenuData && hasPopularWidgets) {
      updatedWidgets = updatedWidgets ? updateWidgetsWithMenuData(updatedWidgets, menuData) : updatedWidgets;
      updatedHomeWidgets = updatedHomeWidgets ? updateWidgetsWithMenuData(updatedHomeWidgets, menuData) : updatedHomeWidgets;
    }

    // Apply combo data updates if available
    if (hasComboData && hasComboWidgets) {
      updatedWidgets = updatedWidgets ? updateWidgetsWithComboData(updatedWidgets, comboData) : updatedWidgets;
      updatedHomeWidgets = updatedHomeWidgets ? updateWidgetsWithComboData(updatedHomeWidgets, comboData) : updatedHomeWidgets;
    }

    const updatedSiteContent = {
      ...siteContent,
      widgets: updatedWidgets,
      homeWidgets: updatedHomeWidgets,
    };

    dispatch(fetchSiteContentSuccess(updatedSiteContent));
  }, [selectedRestaurant, siteContent, menuData, comboData, menuLoading, comboLoading, dispatch]);

  return {
    hasUpdatedWithData: hasUpdatedWithData.current,
  };
};
